'use client';

import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { When, Switch, Case } from 'react-if';
import { useApolloClient } from '@apollo/client';

import { useIsNerdWalletPlusMember } from '@lib/hooks/nerdwallet-plus/use-is-nerdwallet-plus-member';
// eslint-disable-next-line no-restricted-imports
import { Modal } from '@nerdwallet/currency/components/Modal';

import SegmentImpression from '@components/segment-impression';

import canopyFlowReducer, {
  initialFlowState,
  Actions,
  Steps,
  PolicyToReview,
} from './canopy-flow-state';

import TermsOfServiceScreen from './screens/terms-of-service-screen';
import FetchPoliciesScreen from './screens/fetch-policies-screen';
import FetchRatesScreen from './screens/fetch-rates-screen';
import NoPolicyDataErrorScreen from './screens/no-policy-data-error-screen';
import ReviewInformationForm from '@components/review-information-form/review-information-form';
import CanopyFlowContext from './canopy-flow-context';
import CanopyConnect from '../canopy-connect/canopy-connect';
import useStartCanopyFlow from './use-start-canopy-flow';
import PoliciesFoundScreen from './screens/found-policies-screen';

import type { OnExitError, OnExitEventData } from '../canopy-connect/types';

const stepsInsideModal = [
  Steps.CONSENT,
  Steps.FETCH_POLICIES,
  Steps.FETCH_RATES,
  Steps.NO_POLICY_DATA_ERROR,
  Steps.REVIEW_INFORMATION,
  Steps.FOUND_POLICIES,
];

type Props = {
  children: React.ReactNode;
};

const CanopyFlow = ({ children }: Props): React.ReactNode => {
  const apolloClient = useApolloClient();
  const [flowState, dispatch] = useReducer(canopyFlowReducer, initialFlowState);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [skipCanopyStep, setSkipCanopyStep] = useState(false);
  const [reconnectToken, setReconnectToken] = useState<string | undefined>();
  const [onAbortFlowCallback, setOnAbortFlowCallback] = useState<() => void>(
    () => {},
  );
  const [onCompletedFlowCallback, setOnCompletedFlowCallback] = useState<
    () => void
  >(() => {});
  const [isPlatformNative, setIsPlatformNative] = useState(false);

  // `isStartingFlow` prevents this flow from opening multiple times
  const [isStartingFlow, setIsStartingFlow] = useState(false);

  const { isNerdWalletPlusMember } = useIsNerdWalletPlusMember();

  const onDone = useCallback(() => {
    setIsStartingFlow(false);
  }, []);

  const onReady = useCallback(
    (
      hasGivenConsent: boolean,
      isNerdWalletPlusMember?: boolean,
      policyToReview?: PolicyToReview,
    ) => {
      if (skipCanopyStep && isNerdWalletPlusMember && policyToReview) {
        dispatch({
          type: Actions.CHECK_POLICIES_AND_RATES,
          payload: {
            hasPolicyData: true,
            isNerdWalletPlusMember,
            policy: policyToReview,
            hasSeenFoundPolicies: false,
            pullId: undefined,
            noPoliciesFound: false,
          },
        });
      } else {
        dispatch({
          type: Actions.CHECK_CONSENT,
          payload: {
            hasGivenConsent,
          },
        });
      }
      setIsStartingFlow(false);
    },
    [skipCanopyStep],
  );

  useStartCanopyFlow({
    isEnabled: isStartingFlow,
    onDone,
    onReady,
  });

  useEffect(
    function toggleModal() {
      setIsModalOpen(stepsInsideModal.includes(flowState.step));
    },
    [flowState.step],
  );

  const handleCanopyExit = (error: OnExitError, eventData: OnExitEventData) => {
    if (!error && eventData?.pull?.pull_id) {
      dispatch({
        type: Actions.FINISHED_CANOPY_LINKING,
        payload: {
          pullId: eventData.pull.pull_id,
          isNerdWalletPlusMember,
        },
      });
      onCompletedFlowCallback?.();
    } else {
      dispatch({ type: Actions.ABORT });
      onAbortFlowCallback?.();
    }
  };

  const onRequestClose = () => {
    dispatch({
      type: Actions.ABORT,
    });
    apolloClient.refetchQueries({
      updateCache(cache) {
        cache.evict({ fieldName: 'insuranceAccounts' });
        cache.evict({ fieldName: 'insurancePolicies' });
      },
    });
  };

  return (
    <CanopyFlowContext.Provider
      value={{
        startFlow: (params) => {
          setIsStartingFlow(true);
          setSkipCanopyStep(params?.skipCanopyStep || false);
          setReconnectToken(params?.reconnectToken);
          setOnAbortFlowCallback(params?.onAbortFlow);
          setOnCompletedFlowCallback(params?.onCompletedFlow);
          setIsPlatformNative(params?.isPlatformNative);
        },
        isPlatformNative: isPlatformNative,
      }}
    >
      {children}

      <When condition={flowState.step === Steps.CLOSED}>
        {/* Render nothing */}
      </When>

      <When condition={flowState.step === Steps.CANOPY_WIDGET}>
        <SegmentImpression
          eventName="NW Plus Insurance Canopy Flow Widget Viewed"
          eventProps={{
            is_nw_plus_member: isNerdWalletPlusMember,
            is_platform_native: isPlatformNative,
          }}
        >
          <CanopyConnect
            onExit={(error, eventData) => handleCanopyExit(error, eventData)}
            reconnectToken={reconnectToken}
          />
        </SegmentImpression>
      </When>

      <Modal isOpen={isModalOpen} onRequestClose={onRequestClose}>
        <Switch>
          <Case condition={flowState.step === Steps.CONSENT}>
            <SegmentImpression
              eventName="NW Plus Insurance Canopy Flow Consent Screen Viewed"
              eventProps={{
                is_nw_plus_member: isNerdWalletPlusMember,
                is_platform_native: isPlatformNative,
              }}
            >
              <TermsOfServiceScreen dispatch={dispatch} />
            </SegmentImpression>
          </Case>

          <Case condition={flowState.step === Steps.FOUND_POLICIES}>
            <PoliciesFoundScreen
              pullId={flowState.pullId}
              dispatch={dispatch}
            />
          </Case>

          <Case condition={flowState.step === Steps.FETCH_POLICIES}>
            <FetchPoliciesScreen
              pullId={flowState.pullId}
              dispatch={dispatch}
            />
          </Case>

          <Case condition={flowState.step === Steps.FETCH_RATES}>
            <FetchRatesScreen policy={flowState.policy} dispatch={dispatch} />
          </Case>

          <Case condition={flowState.step === Steps.NO_POLICY_DATA_ERROR}>
            <SegmentImpression
              eventName="NW Plus Insurance Canopy Flow Insurance Pull Error Viewed"
              eventProps={{
                is_platform_native: isPlatformNative,
              }}
            >
              <NoPolicyDataErrorScreen dispatch={dispatch} />
            </SegmentImpression>
          </Case>

          <Case condition={flowState.step === Steps.REVIEW_INFORMATION}>
            <SegmentImpression
              eventName="NW Plus Insurance Canopy Flow Your Information Screen Viewed"
              eventProps={{
                policy_id: flowState.policy?.id,
                policy_type: flowState.policy?.policyType,
                carrier_name: flowState.policy?.carrierName,
                is_platform_native: isPlatformNative,
              }}
            >
              <ReviewInformationForm
                onSubmit={() => {
                  dispatch({ type: Actions.CONTINUE });
                }}
                policyId={flowState.policy?.id}
                policyType={flowState.policy?.policyType}
                carrierName={flowState.policy?.carrierName}
              />
            </SegmentImpression>
          </Case>
        </Switch>
      </Modal>
    </CanopyFlowContext.Provider>
  );
};

export default CanopyFlow;
